import _get from 'lodash/get'
import _set from 'lodash/set'
import _noop from 'lodash/noop'
import { AxiosError, AxiosResponse } from 'axios'
import { createContext, ReactElement, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { IUser } from '../../types/'
import logger from '../../helpers/logger'
import { getApiURLAndClientID } from '../../helpers/utils'
import { setCookie, getCookie, removeCookie } from '../../helpers/cookies'
import http, {
  setBaseUrl,
  ejectRequestInterceptor,
  ejectResponseInterceptor,
  setUnauthenticatedCallback,
  setAuthenticationHeaderCallback,
} from '../../helpers/http'

interface AuthContextProps {
  login: (token: string, refreshTokenExpireAt: number, refreshToken: string, user: IUser) => void
  logout: () => void
  isAuthenticated: () => boolean
  getUser: () => IUser | void
}

/* Context */
const AuthContext = createContext<AuthContextProps>({
  login: _noop,
  logout: _noop,
  getUser: _noop,
  isAuthenticated: () => false,
})

AuthContext.displayName = 'AuthContext'

/* Provider */
function AuthProvider(props: { children: ReactElement; cookiePrefix: string }): ReactElement {
  const { children, cookiePrefix } = props

  // restore user env preference & set API BaseURL based on it
  const { apiUrl } = getApiURLAndClientID()
  if (apiUrl) setBaseUrl(apiUrl)

  const authCookieName = cookiePrefix
  const refreshCookieName = `${cookiePrefix}refresh`
  const expirationDateCookieName = `${cookiePrefix}expirationdate`
  const userCookieName = `${cookiePrefix}user`

  const isAuthenticated = (): boolean => {
    return !!getCookie(authCookieName)
  }

  const [, setAuthorized] = useState<boolean>(() => isAuthenticated())

  const login = (token: string, refreshTokenExpireAt: number, refreshToken: string, user: IUser): void => {
    const expirationDateString = refreshTokenExpireAt.toString()
    const userStringify = JSON.stringify(user)
    setCookie(authCookieName, token, refreshTokenExpireAt)
    setCookie(refreshCookieName, refreshToken, refreshTokenExpireAt)
    setCookie(expirationDateCookieName, expirationDateString, refreshTokenExpireAt)
    setCookie(userCookieName, userStringify, refreshTokenExpireAt)
    setAuthorized(true)
  }

  const logout = (): void => {
    removeCookie(authCookieName)
    removeCookie(refreshCookieName)
    removeCookie(expirationDateCookieName)
    removeCookie(userCookieName)
    logger.info('cookies removed')
    setAuthorized(false)
    logger.info('logged out!')
  }

  const getUser = (): IUser | undefined => getCookie(userCookieName) as IUser

  const authContextProps: AuthContextProps = { login, logout, isAuthenticated, getUser }

  // handle token refresh & request retry on error with status 401
  const refreshing = useRef(false)

  //moved from useEffect to fix initial page Get request fail on first render
  const headerInterceptorId = setAuthenticationHeaderCallback((config) => {
    const token = getCookie(authCookieName)
    if (!config.headers?.skipAuth && token) _set(config, 'headers.Authorization', `Bearer ${token}`)

    return config
  })

  useEffect(() => {
    const interceptorId: number = setUnauthenticatedCallback(async (err): Promise<AxiosError | AxiosResponse> => {
      logger.info(`refreshing: ${refreshing.current}`)
      const status = _get(err, 'response.status', null)
      const refreshToken = getCookie(refreshCookieName) as string

      if (!isAuthenticated() || !refreshToken) {
        logout()
      } else if ([401, 403].includes(status) && !refreshing.current && refreshToken) {
        refreshing.current = true
        logger.info(`turn on | refreshing: ${refreshing.current}`)

        return new Promise((_, reject) => {
          http
            .post('/token-refresh', { refreshToken }, { headers: { skipAuth: 'true' } })
            .then((response) => {
              const token = _get(response, 'data.token', null)

              if (token) {
                const expirationDateValue = getCookie(expirationDateCookieName)
                  ? parseInt(getCookie(expirationDateCookieName) as string)
                  : undefined
                setCookie(authCookieName, token, expirationDateValue)
              } else {
                logout()
              }
            })
            .catch(() => {
              logout()
            })
            .finally(() => {
              // delay refreshing flag turn off to avoid double token refresh
              setTimeout(() => {
                refreshing.current = false
                logger.info(`turn off | refreshing: ${refreshing.current}`)
              }, 1000)
              reject(err)
            })
        })
      }

      return Promise.reject(err)
    })

    return (): void => {
      if (interceptorId > -1) ejectResponseInterceptor(interceptorId)
      if (headerInterceptorId > -1) ejectRequestInterceptor(headerInterceptorId)
    }
  })

  return <AuthContext.Provider value={authContextProps}>{children}</AuthContext.Provider>
}

/* useContext */
function useAuth(): AuthContextProps {
  const context = useContext(AuthContext)

  if (context === undefined) {
    throw new Error(`AuthContext must be used within a AuthProvider`)
  }

  return useMemo(() => context || {}, [context])
}

export { AuthProvider, useAuth }
